import { IMeshSectionData, ISlideData } from "@pageBuilder/typings";
import { StringUtil } from "../../../utils/string-util";
import { DomUtil } from "../../../utils/dom-util";
import { MeshSectionRendererBase } from "../mesh-section-renderer-base";
import { SliderNavigationArrowsRenderer } from "./slider-navigation-arrows-renderer";
import { SliderSectionScriptRenderer } from "./slider-section-script-renderer";
import { SliderNavigationDotsRenderer } from "./slider-navigation-dots-renderer";
import { SliderAutoplayRenderer } from "./slider-autoplay-renderer";
import { IRenderingCommonContext } from "@commonRendering/types";

export class MeshSectionRenderer extends MeshSectionRendererBase {

    private readonly navigationArrowsRenderer: SliderNavigationArrowsRenderer;
    private readonly navigationDotsRenderer: SliderNavigationDotsRenderer;
    private readonly autoplayRenderer: SliderAutoplayRenderer;
    private readonly scriptRenderer: SliderSectionScriptRenderer;

    public constructor(renderingCommonContext: IRenderingCommonContext) {
        super(renderingCommonContext);
        this.navigationArrowsRenderer = new SliderNavigationArrowsRenderer(this.styleService);
        this.navigationDotsRenderer = new SliderNavigationDotsRenderer(this.styleService);
        this.autoplayRenderer = new SliderAutoplayRenderer();
        this.scriptRenderer = new SliderSectionScriptRenderer();
    }

    protected createNewSection(): HTMLElement {
        const element = document.createElement("section");
        element.setAttribute("data-section", "");
        element.setAttribute("data-screen-type", "single");

        const sliderContent = document.createElement("div");
        sliderContent.classList.add("slider-content");

        const slidesWrapper = document.createElement("div");
        slidesWrapper.classList.add("slides-wrapper");

        const slide = document.createElement("div");
        slide.id = DomUtil.generateElementId();
        slide.classList.add("slide", "active");

        const content = this.createContentElement();

        const contentBackground = document.createElement("div");
        contentBackground.classList.add("background");

        content.prepend(contentBackground);
        slide.append(content);
        slidesWrapper.append(slide);
        sliderContent.append(slidesWrapper);

        element.append(sliderContent);

        return element;
    }

    protected override renderInternal(element: HTMLElement, data: IMeshSectionData): HTMLElement {
        this.renderSlider(element, data);

        if (data.primaryElement) {
            element.setAttribute("data-primary-element", "");
        }

        element.setAttribute("data-screen-type", data.screenType);

        super.renderInternal(element, data);
        this.renderSectionVisibility(element, data);

        const scrollEffect = data.screenType === "single" ? data.backgroundScrollEffect : "none";
        this.renderBackgroundScrollEffectName(element, scrollEffect);
        this.removeBackgroundInlineStyles(element);

        this.cleanupStylesLegacy(element);

        return element;
    }

    private renderSlider(sectionElement: HTMLElement, data: IMeshSectionData): HTMLElement {
        const existedScreenType = sectionElement.getAttribute("data-screen-type");
        const shouldMigrateMesh = !existedScreenType; // old section with root .background and .content

        const singleScreenContentElement = shouldMigrateMesh ? sectionElement.querySelector(".content") : null;
        const singleScreenBackgroundElement = shouldMigrateMesh ? sectionElement.querySelector(":scope > .background") : null;

        const tempContainer = document.createElement("div"); // for prevent clean up .content and .background elements

        if (singleScreenBackgroundElement) {
            tempContainer.append(singleScreenBackgroundElement);
        }

        if (singleScreenContentElement) {
            tempContainer.append(singleScreenContentElement);
        }

        if (!shouldMigrateMesh) {
            this.cleanupScreen(sectionElement);
        }

        let sliderContent = sectionElement.querySelector<HTMLElement>(".slider-content");
        if (!sliderContent) {
            sliderContent = document.createElement("div");
            sliderContent.classList.add("slider-content");
            sectionElement.prepend(sliderContent);
        }

        let slidesWrapper = sliderContent.querySelector<HTMLElement>(".slides-wrapper");
        if (!slidesWrapper) {
            slidesWrapper = document.createElement("div");
            slidesWrapper.classList.add("slides-wrapper");
            sliderContent.append(slidesWrapper);
        }

        slidesWrapper.querySelectorAll<HTMLElement>(".slide")?.forEach(slideElement => {
            if (!data.slides.find(e => e.id === slideElement.id)) {
                this.cleanupScreen(slideElement);
                slideElement.remove();
            }
        });

        data.slides.forEach(slideData => {
            slideData.id = slideData.id ?? DomUtil.generateElementId();

            let slide = slidesWrapper?.querySelector<HTMLElement>(`#${slideData.id}`);
            if (!slide) {
                slide = document.createElement("div");
                slide.classList.add("slide");
                slide.id = slideData.id;

                if (shouldMigrateMesh) {
                    if (singleScreenBackgroundElement) {
                        slide.append(singleScreenBackgroundElement);
                    }

                    if (singleScreenContentElement) {
                        slide.append(singleScreenContentElement);
                    }
                }

                let setBeforeSlide: HTMLElement | null = null;

                Array.from(slidesWrapper?.querySelectorAll<HTMLElement>(".slide") ?? [])
                    .sort(e => this.getSlideNumber(e))
                    .reverse()
                    .forEach(slideElement => {
                        if (slideData.number && this.getSlideNumber(slideElement) > slideData.number) {
                            setBeforeSlide = slideElement;
                        }
                    });

                if (setBeforeSlide) {
                    (setBeforeSlide as HTMLElement).before(slide);
                } else {
                    slidesWrapper?.append(slide);
                }
            }

            if (data.screenType === "single" || shouldMigrateMesh) {
                slide.classList.toggle("active", true);
            }

            slide.setAttribute("data-number", (slideData.number ?? 1).toString());

            this.renderScreen(slide, slideData);
        });

        tempContainer.remove();
        if (data.screenType === "slider") {
            if (data.navigationArrows) {
                this.navigationArrowsRenderer.render(sectionElement, data.navigationArrows);
            } else {
                this.navigationArrowsRenderer.cleanup(sectionElement);
            }

            if (data.navigationDots) {
                this.navigationDotsRenderer.render(sectionElement, data.navigationDots);
            } else {
                this.navigationDotsRenderer.cleanup(sectionElement);
            }

            if (data.autoplay) {
                this.autoplayRenderer.render(sectionElement, data.autoplay, this.getRenderingMode());
            } else {
                this.autoplayRenderer.cleanup(sectionElement);
            }

            this.scriptRenderer.renderCommon(this.renderedDocument);
        } else {
            this.removeSliderElements(sectionElement);
        }

        this.ensureSlideOrder(sectionElement, data.slides);

        return sectionElement;
    }

    private cleanupStylesLegacy(element: HTMLElement): void {
        this.styleService.clearStyle(element, `#${element.id} .background`);
        this.styleService.clearStyle(element, `#${element.id} .background img`);
        this.styleService.clearStyle(element, `#${element.id} .content .background`);
        this.styleService.clearStyle(element, `#${element.id} .content .background img`);
    }

    protected cleanupInternal(element: HTMLElement): void {
        this.cleanupSlider(element);
        this.cleanupObsoleteSectionStyles(element);
    }

    private cleanupSlider(sectionElement: HTMLElement): void {
        const sliderContent = sectionElement.querySelector<HTMLElement>(".slider-content");
        const slidesWrapper = sliderContent?.querySelector<HTMLElement>(".slides-wrapper");
        const slides = slidesWrapper?.querySelectorAll<HTMLElement>(".slide");

        slides?.forEach(slideElement => {
            this.cleanupScreen(slideElement);
            slideElement.remove();
        });

        slidesWrapper?.remove();
        sliderContent?.remove();

        this.autoplayRenderer.cleanup(sectionElement);
        this.cleanupSliderScript();
    }

    private cleanupObsoleteSectionStyles(element: HTMLElement): void {
        const selectors = super.getContentSelectorsInternal(element);
        selectors.forEach(selector => this.styleService.clearStyle(element, selector));
    }

    protected override getContentSelectorsInternal(element: HTMLElement): Array<string> {
        const selectors = [`#${element.id}`];

        selectors.push(`#${element.id} .content`); // for old sections (created before slider release)

        const slides = element.querySelectorAll<HTMLElement>(".slide");
        slides?.forEach(slideElement => {
            selectors.push(...super.getContentSelectorsInternal(slideElement));
        });

        selectors.push(...this.navigationArrowsRenderer.getAllContentSelectors(element));
        selectors.push(...this.navigationDotsRenderer.getAllContentSelectors(element));

        return selectors;
    }

    private ensureSlideOrder(sectionElement: HTMLElement, slides: ISlideData[]): void {
        const slidesWrapper = sectionElement.querySelector<HTMLElement>(".slides-wrapper");
        if (!slidesWrapper) {
            return;
        }

        const dotsWrapper = sectionElement.querySelector(".navigation-dots-wrapper");

        let numberActiveSlide = 0;
        const slidesBuffer: HTMLElement[] = [];
        const dotsBuffer: HTMLElement[] = [];

        slides.forEach(slideData => {
            const slide = slidesWrapper?.querySelector<HTMLElement>(`#${slideData.id}`);
            if (slide) {
                slidesBuffer.push(slide);

                if (!numberActiveSlide && slide.classList.contains("active") && slideData.number) {
                    numberActiveSlide = slideData.number;
                }
            }

            const dot = dotsWrapper?.querySelector<HTMLElement>(`[data-number="${slideData.number}"]`);
            if (dot) {
                dotsBuffer.push(dot);
            }
        });

        if (!numberActiveSlide) {
            numberActiveSlide = slides[0].number ?? 1;
        }

        slidesWrapper.replaceChildren();
        dotsWrapper?.replaceChildren();

        slidesBuffer.forEach(slide => {
            slidesWrapper.append(slide);
        });

        dotsBuffer.forEach(dot => {
            dotsWrapper?.append(dot);
        });

        const targetDot = dotsWrapper?.querySelector(`[data-number="${numberActiveSlide}"]`);
        targetDot?.classList.add("active");

        const activeSlide = slidesWrapper.querySelector(`.slide[data-number="${numberActiveSlide}"]`);
        activeSlide?.classList.add("active");
    }

    private getSlideNumber(slideElement: HTMLElement): number {
        return StringUtil.getNumberFromStr(slideElement.getAttribute("data-number") ?? "1", 1);
    }

    private removeSliderElements(slideElement: HTMLElement): void {
        this.navigationArrowsRenderer.cleanup(slideElement);
        this.navigationDotsRenderer.cleanup(slideElement);
        this.autoplayRenderer.cleanup(slideElement);
    }

    private cleanupSliderScript(): void {
        if (!document.querySelector("[data-section][data-screen-type=slider]")) {
            this.scriptRenderer.cleanupCommon(this.renderedDocument);
        }
    }

}
